import { VArray, Variable, all } from 'alkali'
import Abstract from './Abstract'
import * as _ from 'lodash'
import { ReferencedTerms } from './extraction'
import { Extraction } from 'services/extract/types'
import Annotations, {
	Annotation,
	TypedRange,
	sort,
	findSection, findSentence, compare, compareLength, compareSource, comparePrecedence
} from './Annotations'
import {
	AbstractAnnotator,
	AnnotationStack,
	Annotator,
	DocSearchAnnotator,
	NCTIdAnnotator,
	RxNormQuantifiers,
	TimePointAnnotator,
	AnnotationLayer,
	ContextFilter,
	RXNormAnnotations,
	MethodsFollowupSentences,
	InInterventionSentences,
	InOutcomeSentences,
	GroupN,
	BinaryValue,
	PValue, Quantity, CI, SD, ContinuousValue, AValue, DosageAnnotator, annotatorsFor, InRelatedOutcomeSentences
} from './Annotators'
import {
	RelateInterventionGroupN, RelateInterventionGroupNPosNumbers,
	RelateInterventionQuantifiers,
	RelateOutcomeQuantities, RelateTimePointQuantities
} from './Correlators'
import Extractor from './Extractor'

// export const INTERVENTION_ENTITIES = [QUANTIFIER /*, QUANTITY*/]
// export const CLIENT_ENTITIES = [GROUPN, PVALUE, ...INTERVENTION_ENTITIES]

function groupByMany(collection, fn: (item) => string[]) {
	const grouped = {}
	for (const item of collection) {
		for (const key of fn(item)) {
			(grouped[key] || (grouped[key] = [])).push(item)
		}
	}
	return grouped
}

export function annotate(abstract: AnnotatedAbstract, annotators: AnnotationPass[], contexts: TypedRange[] = abstract.abstract.sentences, layers: AnnotationLayer[] = [], layerPos = layers.length): AnnotationLayer[] {
	const stack = { layers: [] } as AnnotationStack
	for (const prior of layers) {
		stack.layers.push(prior)
		stack[prior.name] = prior
	}
	const processed = new Map<any, Set<any>>()
	// perform annotation
	for (const pass of annotators) {
		const [selector, annotator] = pass
		let selected = selector ? selector(contexts, stack) : contexts
		// avoid rerunning the same annotator on the same contexts
		let processedCtxs = processed.get(annotator.constructor)
		if (!processedCtxs) {
			processedCtxs = new Set(selected)
			processed.set(annotator.constructor, processedCtxs)
		} else {
			selected = selected.filter(ctx => !processedCtxs.has(ctx) && processedCtxs.add(ctx))
		}
		const layer = annotator.annotate(abstract, selected, stack)
		for (const a of layer.annotations) {
			a.layer = layerPos
		}
		stack.layers.push(layer)
		stack[layer.name] = layer
		layerPos++
	}

	return stack.layers.slice(layers.length)
}

function assignPrimary(annots: Annotation[]) {
	//pinnedAnnotations.forEach(a => a.primary = true)
	// group by same type and related id or exact text, within same group
	const annotationRelationKeys = a =>
		/* TODO: need to correlate || master term id ?*/
		((a.senses && a.senses.length) ? a.senses : [a.text]).map(s => a.type + '-' + s)

	// since annotations are sorted, the first annotations in group will be first positional annotation
	const relatedAnnotations = groupByMany(annots, a => annotationRelationKeys(a))
	//const relatedSectionAnnotations = groupByMany(annots.filter(a => a.section), a => annotationRelationKeys(a).map(k => a.section.label + '-' + k))
	const prunedTypes = ['intervention', 'outcome', 'characteristic']
	// assign a primary for related annotations in a particular section
	for (const key in relatedAnnotations) {
		const related = relatedAnnotations[key]
		if (!prunedTypes.includes(related[0].type)) continue
		// if this is the first annotation of its related group in the section, mark it as 'primary'
		// if no annotations in relation group in this particular section are marked primary (e.g. have been pinned already)
		// mark the first one as the primary
		if (!related.some(a => a.primary)) {
			related[0].primary = true
		}
	}
}

const USER_LAYER = 0

type AnnotationPass = [ContextFilter | undefined, Annotator]

/**
 * Wrapper around Abstract text/sentences, which applies derived client-side annotations, as well as those
 * manually created by the user via selection.
 * @see Abstract
 * @see Annotator
 * @see Correlator
 */
export default class AnnotatedAbstract {
	readonly systemAnnotations = new VArray<Annotation>([])
	readonly userAnnotations = new VArray<Annotation>([])
	readonly annotations: Variable<Annotation[]>
	readonly visibleAnnotations: Variable<Annotation[]>
	private layers: AnnotationLayer[]

	private annotationsBySection = new Map()
	private annotationsBySentence = new Map()

	constructor(readonly abstract: Abstract,
	            private readonly docsearchAnnotations: Annotation[],
	            readonly referencedTerms: ReferencedTerms,
	            readonly showAllDS = false, onlyPICO = false) {
		//this.referencedTerms = (this.referencedTerms || []).filter(t => ['intervention', 'outcome'].includes(t.type))
		const result = this.annotate(onlyPICO)
		this.layers = result.layers
		this.systemAnnotations.put(result.annotations)
		this.annotations = all([this.systemAnnotations, this.userAnnotations]).to(([systemAnnotations, userAnnotations]) => {
			const annotations = systemAnnotations.concat(userAnnotations)
			sort(annotations)
			for (let i = 0; i < annotations.length; i++) {
				annotations[i].i = i
			}
			//assignPrimary(annotations)
			// hide any quantities that don't have a parent, or aren't associated with the primary of related annotations
			// for (const q of annotations.filter(a => a.source != 'user' && ['binary-value', 'measure', 'quantity'].includes(a.type))) {
			//   if (!q.parent /* || !q.parent.primary*/) {
			//     q.visual = false
			//   }
			// }
			// hide characteristics
			/*for (const a of annotations.filter(a => a.source != 'user' && a.types.includes('characteristic'))) {
				a.visual = false
			}*/
			return annotations
		})
		this.visibleAnnotations = this.annotations.to(annotations => this.cull(annotations))
		//@ts-ignore
		this.annotationsBySection = this.annotations.as(VArray).groupBy(a => findSection(a))
		//@ts-ignore
		this.annotationsBySentence = this.annotations.as(VArray).groupBy(a => findSentence(a))
	}

	autoExtract(extraction) {
		if (Extractor.extract(extraction, this)) {
			this.removeExtraneousAnnotations(extraction)
			return true
		}
	}

	private removeExtraneousAnnotations(extraction: Extraction) {
		// if treatments and their values have been set, then hide all other unreferenced treatment annotations
		let remove = []
		const referenced = []
		let numItvns = 0
		let numItvnsAssigned = 0
		for (const group of extraction.groups || []) {
			for (const intervention of group.interventions || []) {
				numItvns++
				if (intervention.name?.a) {
					numItvnsAssigned++
					referenced.push(intervention.name.a)
					// if this intervention is fully assigned, then hide other all related annotations
					if (group?.n?.val && intervention?.name?.val) {
						console.log('should hide annotations related to itvn:', intervention.name.a.related)
						referenced.push(group.n?.a)
						referenced.push(...intervention.name.a.measures || [])
						const treatment = intervention.treatments[0]
						if (treatment) {
							referenced.push(treatment?.measure?.a)
						}
						remove.push(...intervention.name.a.related)
					}
				}
			}
		}
		if (numItvns > 0 && numItvns == numItvnsAssigned) {
			// all interventions are assigned, remove all nonreferenced interventions
			remove.push(this.systemAnnotations.valueOf().filter(a => a.types.includes('intervention')))
		}
		let numOutcomeValues = 0
		let numOutcomeValuesAssigned = 0
		const groups = extraction.groups || []
		for (const outcome of extraction.outcomes) {
			if (!outcome.name) continue
			if (outcome.name?.a) {
				referenced.push(outcome.name.a)
				if (!groups.length) continue
				const cellValues = groups.map(group => outcome.cells[group.id])
				numOutcomeValues += cellValues.length
				if (cellValues.every(v => !!v)) {
					numOutcomeValuesAssigned += cellValues.length
					referenced.push(...cellValues.map(v => v?.a))
					// hide all other related outcomes
					console.log('should hide annotations related to outcome:', outcome.name.a.related)
					remove.push(...outcome.name.a.related)
				}
			}
		}
		if (numOutcomeValues > 0 && numOutcomeValues == numOutcomeValuesAssigned) {
			// all outcome values are assigned, remove all nonreferenced outcomes
			remove.push(...this.systemAnnotations.valueOf().filter(a => a.types.includes('outcome')))
		}
		// remove all extraneous annotations and related measures
		remove = _.difference(_.compact(remove), _.compact(referenced))
		if (remove.length) {
			const measures = _.compact(remove.flatMap(a => a.measures))
			for (const a of this.annotations.valueOf()) {
				if (a.related) a.related = _.without(a.related, ...remove)
			}
			this.deleteAnnotations(remove.concat(measures))
		}
	}

	annotate(onlyPICO = false) {
		const annotators: AnnotationPass[] = [
			[undefined, new AbstractAnnotator()],
			[undefined, new DocSearchAnnotator(this.abstract, this.docsearchAnnotations, this.showAllDS ? null : this.referencedTerms)],
			[undefined, new NCTIdAnnotator()]
		]
		if (!onlyPICO) {
			annotators.push(
			//...termScanners.map(scanner => new LocalAnnotator(scanner, SectionFilter(CLASS_SECTION_MAP[scanner.type]))),
			//...scanners.map(scanner => new LocalAnnotator(scanner))
			[undefined, new PValue()], // make sure pvalues take precedence over other overlapping annotations
			[undefined, new AValue()], // make sure pvalues take precedence over other overlapping annotations
			[undefined, new CI()],
			// additionally try to parse (non-visual) quantifiers out of RXNorm drug dose annotations
			[RXNormAnnotations, new RxNormQuantifiers()],
			[MethodsFollowupSentences, new TimePointAnnotator()],
			[InInterventionSentences, new GroupN()],
			//[InOutcomeSentences, new GroupN()],
			//[InOutcomeSentences, this.type == 'Binary' ? new BinaryValue() : new ContinuousValue()],
			[InRelatedOutcomeSentences(this.referencedTerms?.binaryOutcomes), new BinaryValue()],
			[InRelatedOutcomeSentences(this.referencedTerms?.ctnsOutcomes), new ContinuousValue()],
			[InInterventionSentences, new DosageAnnotator()], // intervention dosages
			[InInterventionSentences, new Quantity()]
			)
		}

		const layers = annotate(this, annotators)
		const annotations = layers.flatMap(layer => layer.annotations)
		//this.cull(annotations)
		sort(annotations)
		for (let i = 0; i < annotations.length; i++) {
			annotations[i].i = i
		}
		new RelateInterventionGroupN().correlate(this, annotations)
		new RelateOutcomeQuantities().correlate(this, annotations)
		new RelateInterventionQuantifiers().correlate(this, annotations)
		new RelateInterventionGroupNPosNumbers().correlate(this, annotations)
		new RelateTimePointQuantities().correlate(this, annotations)

		return { layers, annotations }
	}

	annotateLocal<T extends TypedRange>(context: T, types) {
		console.log('user annotating local types:', types)
		const annotators = annotatorsFor(types).map(a => [undefined, a] as AnnotationPass)
		const userLayer = { name: 'user', annotations: this.userAnnotations.valueOf() }
		const layers = annotate(this, annotators, [context], [userLayer].concat(this.layers), USER_LAYER)
		const newAnnotations = layers.flatMap(layer => layer.annotations)
		for (const a of newAnnotations) {
			a.source = 'user'
		}
		const userAnnotations = this.userAnnotations.valueOf().concat(newAnnotations)
		userAnnotations.sort()
		this.userAnnotations.put(userAnnotations)
		return newAnnotations
	}

	deleteAnnotations(a) {
		// this.systemAnnotations.updated()
		// this.userAnnotations.updated()
		this.systemAnnotations.put(_.without(this.systemAnnotations.valueOf(), ...a))
		this.userAnnotations.put(_.without(this.userAnnotations.valueOf(), ...a))
	}

	private cull(annotations: Annotation[]) {
		// perform back-to-front culling for visual annotations/ranges
		// for each layer of the stack:
		// * remove user-pinned annotations:
		//   * remove identical deleted annotations
		//   * remove overlapping saved/existing annotations
		// * append existing annotations to result, accumulating as current annotations
		const visual = annotations.filter(a => a.visual != false && !a.deleted)
		const a = new Annotations(visual).removeOverlapping(compare(compareSource, comparePrecedence, compareLength))
		a.sort()
		let annots = a.getIntervals()
		const hide = ['p-value', 'a-value', 'ci']
		// hide p-values //in binary extraction
		//if (this.type != 'Continuous') {
		return annots.filter(a => !(a.types || []).some(t => hide.includes(t)))
		//}
	}

	static getAnnotations(annotations, selected, picoType) {
		let matchingAttributes = []
		let matchedAttributeByText = new Map()
		for (let annotation of annotations) {
			if (annotation.types.includes(picoType)) {
				let bestAttribute, bestScore = 0
				for (let attribute of selected) {
					do {
						let ids = attribute.ids
						if (!ids)
							continue
						let score = 0
						for (let id of ids) {
							if (annotation.senses?.includes(id))
								score += 1 / ids.length
						}
						if (score > bestScore) {
							bestScore = score
							bestAttribute = attribute
						}
					} while (attribute = attribute.or)
				}
				if (bestAttribute) {
					let matchedAttribute = matchedAttributeByText.get(annotation.text.toLowerCase())
					if (matchedAttribute) {
						matchedAttribute.matchingScore = 1 - (1 - matchedAttribute.matchingScore) * (1 - bestScore / 4)
					} else {
						annotation.analyzeAs = bestAttribute.label
						annotation.matchingScore = bestScore / 4
						annotation.name = annotation.text
						matchingAttributes.push(annotation)
						matchedAttributeByText.set(annotation.text.toLowerCase(), annotation)
					}
				}
			}
		}
		return matchingAttributes
	}
}